import React from "react";
import type { ReactElement } from "react";

import type { CompositeNavigationProp, RouteProp } from "@react-navigation/native";
import { createStackNavigator } from "@react-navigation/stack";
import type { StackNavigationProp } from "@react-navigation/stack";

import type { HubTabNavigatorProps } from "pianofunclub-crm/navigation/HubNavigator";
import defaultNavOptions from "pianofunclub-crm/navigation/utils/defaultNavigationOptions";
import type {
  AccountParams,
  DefaultTermDatesParams,
  EnrolmentParams,
  InvoiceParams,
  SchoolParams,
} from "pianofunclub-crm/navigation/utils/screenParams";
import AccountScreen, {
  screenOptions as accountScreenOptions,
} from "pianofunclub-crm/screens/accounts/AccountScreen";
import InvoiceScreen, {
  screenOptions as invoiceScreenOptions,
} from "pianofunclub-crm/screens/invoicing/InvoiceScreen";
import InvoicingHubScreen, {
  screenOptions as invoicingHubScreenOptions,
} from "pianofunclub-crm/screens/invoicing/InvoicingHubScreen";
import DefaultTermDatesScreen, {
  screenOptions as defaultTermDatesScreenOptions,
} from "pianofunclub-crm/screens/schools/DefaultTermDatesScreen";
import SchoolScreen, {
  screenOptions as schoolScreenOptions,
} from "pianofunclub-crm/screens/schools/SchoolScreen";
import EnrolmentScreen, {
  screenOptions as enrolmentScreenOptions,
} from "pianofunclub-crm/screens/waitingList/EnrolmentScreen";

export type InvoicingStackNavigatorParamList = {
  Account: AccountParams;
  DefaultTermDates: DefaultTermDatesParams;
  Enrolment: EnrolmentParams;
  Invoice: InvoiceParams;
  InvoicingHub?: {
    block?: number;
    orderBy?: string;
    schoolId?: string;
    searchTerm?: string;
    startingYear?: number;
    status?: string;
  };
  School: SchoolParams;
};

type NavigatorProps<T extends keyof InvoicingStackNavigatorParamList> = StackNavigationProp<
  InvoicingStackNavigatorParamList,
  T
>;

export type InvoicingStackNavigatorProps<T extends keyof InvoicingStackNavigatorParamList> =
  CompositeNavigationProp<NavigatorProps<T>, HubTabNavigatorProps<"InvoicingHubTab">>;

export type InvoicingStackRouteProps<T extends keyof InvoicingStackNavigatorParamList> = RouteProp<
  InvoicingStackNavigatorParamList,
  T
>;

const InvoicingStackNavigator = createStackNavigator<InvoicingStackNavigatorParamList>();

const InvoicingNavigator = (): ReactElement => {
  return (
    <InvoicingStackNavigator.Navigator screenOptions={defaultNavOptions}>
      <InvoicingStackNavigator.Screen
        component={InvoicingHubScreen}
        name="InvoicingHub"
        options={invoicingHubScreenOptions}
      />
      <InvoicingStackNavigator.Screen
        component={InvoiceScreen}
        name="Invoice"
        options={invoiceScreenOptions}
      />
      <InvoicingStackNavigator.Screen
        component={SchoolScreen}
        name="School"
        options={schoolScreenOptions}
      />
      <InvoicingStackNavigator.Screen
        component={AccountScreen}
        name="Account"
        options={accountScreenOptions}
      />
      <InvoicingStackNavigator.Screen
        component={DefaultTermDatesScreen}
        name="DefaultTermDates"
        options={defaultTermDatesScreenOptions}
      />
      <InvoicingStackNavigator.Screen
        component={EnrolmentScreen}
        name="Enrolment"
        options={enrolmentScreenOptions}
      />
    </InvoicingStackNavigator.Navigator>
  );
};

export default InvoicingNavigator;
